import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGiftAgentWorkspaceUrl,
  getProjectWorkflowUrl,
  getSessionOpenUrl,
  isPmCapableRunner,
  isTeamWorkflowRunner,
} from '@/lib/gift-agent';
import { toast } from 'sonner';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type {
  Project,
  Session,
  ProjectTemplate,
} from '@claude-code-api/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { ProjectCard } from '@/components/ProjectCard';
import { CreateProjectButton } from '@/components/CreateProjectButton';
import { StartSessionModal } from '@/components/StartSessionModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { buildTemplateCatalogApiPath } from '@/lib/template-catalog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  Play,
  Database,
  FolderGit2,
  FileCode,
  File,
  MoreHorizontal,
  Trash2,
  ExternalLink,
  LayoutGrid,
  List,
  Users,
  Workflow,
} from 'lucide-react';

type ViewMode = 'grid' | 'list';

export default function ProjectsPage() {
  const { t, i18n } = useTranslation(['projects', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Templates state (for display in list view)
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);

  // Session modal state
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [startingSession, setStartingSession] = useState<string | null>(null);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [keepDatabase, setKeepDatabase] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [autoOpenTemplateId, setAutoOpenTemplateId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    fetchData();
    fetchTemplates();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openCreate') !== '1') return;
    const templateId = params.get('template');
    setAutoOpenTemplateId(templateId || '__default__');
  }, [location.search]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(
        buildTemplateCatalogApiPath(
          '/api/templates?includeDisabled=1',
          i18n.resolvedLanguage || i18n.language,
        ),
        { credentials: 'include' },
      );
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error(t('projects:errors.loadTemplates'));
    }
  };

  const fetchData = async () => {
    try {
      const [projectsRes, sessionsRes] = await Promise.all([
        fetch('/api/projects', { credentials: 'include' }),
        fetch('/api/sessions', { credentials: 'include' }),
      ]);

      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects(data.data);
      }

      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('projects:errors.loadProjects'));
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCreated = (project: Project) => {
    setProjects([project, ...projects]);
  };

  const handleSessionCreated = (session: Session) => {
    setSessions([session, ...sessions]);
  };

  const handleStartClick = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;

    if (project.isTeamMode) {
      navigate(`/projects/workflow/${projectId}`);
    } else {
      // For regular projects, show runner selection modal
      setSelectedProjectId(projectId);
      setStartingSession(projectId);
    }
  };

  const handleModalClose = () => {
    setSelectedProjectId(null);
    setStartingSession(null);
  };

  const openDeleteModal = (project: Project) => {
    setProjectToDelete(project);
    setKeepDatabase(false);
    setShowDeleteModal(true);
  };

  const deleteProject = async () => {
    if (!projectToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(
        `/api/projects/${projectToDelete.id}?keepDatabase=${keepDatabase}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );

      if (response.ok) {
        setProjects(projects.filter((p) => p.id !== projectToDelete.id));
        toast.success(t('projects:messages.projectDeleted'));
        setShowDeleteModal(false);
        setProjectToDelete(null);
      } else {
        const data = await response.json();
        toast.error(data.error || t('projects:errors.deleteProject'));
      }
    } catch (err) {
      console.error('Failed to delete project:', err);
      toast.error(t('projects:errors.deleteProject'));
    } finally {
      setDeleting(false);
    }
  };

  const getActiveSessionForProject = (projectId: string) => {
    return sessions.find(
      (s) => s.projectId === projectId && s.status === 'running',
    );
  };

  const handleAutoOpenHandled = () => {
    setAutoOpenTemplateId(null);
    if (!location.search) return;
    navigate('/projects', { replace: true });
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  };

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(search.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('projects:list.title')}
          description={t('projects:list.description')}
          action={
            <CreateProjectButton
              onProjectCreated={handleProjectCreated}
              autoOpenTemplateId={autoOpenTemplateId}
              onAutoOpenHandled={handleAutoOpenHandled}
            />
          }
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card border rounded-lg p-5 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('projects:list.title')}
        description={t('projects:list.description')}
        action={
          <CreateProjectButton
            onProjectCreated={handleProjectCreated}
            autoOpenTemplateId={autoOpenTemplateId}
            onAutoOpenHandled={handleAutoOpenHandled}
          />
        }
      />

      {/* Search and View Toggle */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('projects:list.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            aria-label={t('projects:list.searchAriaLabel')}
          />
        </div>
        <div className="flex items-center border rounded-lg p-1">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="h-8 w-8 p-0"
            aria-label={t('projects:list.gridView')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="h-8 w-8 p-0"
            aria-label={t('projects:list.listView')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Projects Grid View */}
      {viewMode === 'grid' && (
        <>
          {filteredProjects.length === 0 ? (
            <EmptyState
              icon={FolderGit2}
              title={
                search
                  ? t('projects:list.emptyFilteredTitle')
                  : t('projects:list.emptyTitle')
              }
              description={
                search
                  ? t('projects:list.emptyFilteredDescription')
                  : t('projects:list.emptyDescription')
              }
              action={
                !search ? (
                  <CreateProjectButton
                    onProjectCreated={handleProjectCreated}
                    autoOpenTemplateId={autoOpenTemplateId}
                    onAutoOpenHandled={handleAutoOpenHandled}
                  />
                ) : undefined
              }
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredProjects.map((project) => {
                const activeSession = getActiveSessionForProject(project.id);
                const template = templates.find(
                  (t) => t.id === project.templateId,
                );

                return (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    activeSession={activeSession}
                    template={template}
                    startingSession={startingSession}
                    onStart={handleStartClick}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Projects List View */}
      {viewMode === 'list' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('projects:list.table.project')}</TableHead>
                  <TableHead>{t('projects:list.table.project')}</TableHead>
                  <TableHead>{t('projects:list.table.type')}</TableHead>
                  <TableHead>{t('projects:list.table.database')}</TableHead>
                  <TableHead>{t('projects:list.table.status')}</TableHead>
                  <TableHead>{t('projects:list.table.created')}</TableHead>
                  <TableHead className="text-right">
                    {t('projects:list.table.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <div className="mx-auto w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
                        <FolderGit2 className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground">
                        {search
                          ? t('projects:list.emptyFilteredTitle')
                          : t('projects:list.emptyTitle')}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProjects.map((project) => {
                    const activeSession = getActiveSessionForProject(
                      project.id,
                    );
                    const template = templates.find(
                      (t) => t.id === project.templateId,
                    );

                    return (
                      <TableRow key={project.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                project.templateId === 'nextjs-starter'
                                  ? 'bg-black text-white'
                                  : project.templateId === 'vite-react'
                                    ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                                    : project.gitUrl
                                      ? 'bg-foreground text-background'
                                      : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {project.gitUrl ? (
                                <FolderGit2 className="w-4 h-4" />
                              ) : project.templateId === 'nextjs-starter' ? (
                                <svg
                                  className="w-4 h-4"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M11.572 0c-.176 0-.31.001-.358.007a19.76 19.76 0 0 1-.364.033C7.443.346 4.25 2.185 2.228 5.012a11.875 11.875 0 0 0-2.119 5.243c-.096.659-.108.854-.108 1.747s.012 1.089.108 1.748c.652 4.506 3.86 8.292 8.209 9.695.779.251 1.6.422 2.534.525.363.04 1.935.04 2.299 0 1.611-.178 2.977-.577 4.323-1.264.207-.106.247-.134.219-.158-.02-.013-.907-1.205-1.973-2.647l-1.938-2.622-2.429-3.593c-1.336-1.976-2.436-3.592-2.446-3.592-.009 0-.018 1.598-.022 3.551-.005 3.418-.01 3.561-.058 3.643-.068.116-.12.162-.24.207-.093.035-.175.042-.611.042h-.499l-.126-.078a.303.303 0 0 1-.108-.117l-.035-.078.004-4.751.006-4.752.082-.105a.44.44 0 0 1 .117-.104c.107-.055.151-.058.665-.058.57 0 .661.015.793.131.037.033 1.351 2.023 2.922 4.425l4.712 7.182 1.019 1.549.103-.068c.912-.595 1.877-1.447 2.635-2.326a11.879 11.879 0 0 0 2.773-6.118c.096-.66.108-.854.108-1.747 0-.894-.012-1.088-.108-1.747-.652-4.506-3.86-8.292-8.209-9.695a12.231 12.231 0 0 0-2.498-.523c-.203-.02-.407-.028-.593-.028z" />
                                </svg>
                              ) : project.templateId === 'vite-react' ? (
                                <FileCode className="w-4 h-4" />
                              ) : (
                                <File className="w-4 h-4" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Link
                                  to={`/projects/${project.id}`}
                                  className="font-medium hover:text-primary transition-colors"
                                >
                                  {project.name}
                                </Link>
                                {project.isShared && (
                                  <Badge
                                    variant="secondary"
                                    className="gap-1 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 text-[10px] px-1.5 py-0"
                                  >
                                    <Users className="w-2.5 h-2.5" />
                                    {t('projects:list.shared')}
                                  </Badge>
                                )}
                              </div>
                              {project.isShared && project.ownerName ? (
                                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                  {t('projects:list.sharedBy', {
                                    owner: project.ownerName,
                                  })}
                                </p>
                              ) : project.gitUrl ? (
                                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                  {project.gitUrl.replace(
                                    'https://github.com/',
                                    '',
                                  )}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {template ? (
                            <Badge
                              variant="secondary"
                              className="bg-primary/10 text-primary"
                            >
                              {template.name}
                            </Badge>
                          ) : project.gitUrl ? (
                            <Badge variant="secondary" className="gap-1">
                              <FolderGit2 className="w-3 h-3" />
                              {t('projects:overview.github')}
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              {t('projects:list.blank')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {project.hasDatabase ? (
                            <Badge
                              variant="secondary"
                              className="gap-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                            >
                              <Database className="w-3 h-3" />
                              {t('projects:overview.postgresActive')}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {t('projects:list.none')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {activeSession ? (
                            <div className="flex items-center gap-2">
                              <StatusIndicator status="running" />
                              <span className="text-sm text-green-600">
                                {t('projects:list.running')}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              {t('projects:list.stopped')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {formatDate(project.createdAt)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {activeSession ? (
                              isTeamWorkflowRunner(activeSession.runnerType) ? (
                                <Button
                                  asChild
                                  variant="outline"
                                  size="sm"
                                  className="text-green-700 border-green-200 bg-green-50 hover:bg-green-100 dark:text-green-300 dark:border-green-800 dark:bg-green-950 dark:hover:bg-green-900"
                                >
                                  <a
                                    href={getProjectWorkflowUrl(project.id)}
                                    onClick={() => {
                                      navigate(
                                        getProjectWorkflowUrl(project.id),
                                      );
                                    }}
                                  >
                                    {t('projects:list.openWorkflow')}
                                  </a>
                                </Button>
                              ) : (
                                <Button
                                  asChild
                                  variant="outline"
                                  size="sm"
                                  className="text-green-700 border-green-200 bg-green-50 hover:bg-green-100 dark:text-green-300 dark:border-green-800 dark:bg-green-950 dark:hover:bg-green-900"
                                >
                                  <a
                                    href={
                                      isPmCapableRunner(
                                        activeSession.runnerType,
                                      )
                                        ? getGiftAgentWorkspaceUrl(
                                            activeSession.id,
                                          )
                                        : getSessionOpenUrl(activeSession.id)
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => {
                                      navigate(`/sessions/${activeSession.id}`);
                                    }}
                                  >
                                    {t('projects:list.open')}
                                  </a>
                                </Button>
                              )
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleStartClick(project.id)}
                                disabled={startingSession === project.id}
                                aria-label={
                                  project.isTeamMode
                                    ? t('projects:list.openWorkflow')
                                    : t('projects:list.startSession')
                                }
                              >
                                {startingSession === project.id ? (
                                  <Spinner className="h-3 w-3" />
                                ) : (
                                  <>
                                    {project.isTeamMode ? (
                                      <Workflow className="w-4 h-4" />
                                    ) : (
                                      <Play className="w-4 h-4" />
                                    )}
                                  </>
                                )}
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  aria-label={t('projects:list.moreActions')}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link to={`/projects/${project.id}`}>
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    {t('projects:list.viewDetails')}
                                  </Link>
                                </DropdownMenuItem>
                                {!project.isShared && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => openDeleteModal(project)}
                                      className="text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      {t('projects:list.delete')}
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        title={t('projects:list.deleteDialog.title')}
        description={t('projects:list.deleteDialog.description', {
          name: projectToDelete?.name || '',
        })}
        confirmLabel={t('projects:list.delete')}
        cancelLabel={t('common:cancel')}
        onConfirm={deleteProject}
        loading={deleting}
        variant="destructive"
      >
        {projectToDelete?.hasDatabase && (
          <div className="p-4 bg-muted/50 rounded-xl border">
            <div className="flex items-start gap-3">
              <Checkbox
                id="keepDatabase"
                checked={keepDatabase}
                onCheckedChange={(checked) => setKeepDatabase(checked === true)}
                className="mt-1"
              />
              <div className="flex-1">
                <Label
                  htmlFor="keepDatabase"
                  className="cursor-pointer flex items-center gap-2"
                >
                  <Database className="w-4 h-4 text-emerald-600" />
                  {t('projects:list.deleteDialog.keepDatabase')}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('projects:list.deleteDialog.keepDatabaseHelp')}
                </p>
              </div>
            </div>
          </div>
        )}
      </ConfirmDialog>

      {/* Start Session Modal */}
      <StartSessionModal
        projectId={selectedProjectId}
        onClose={handleModalClose}
        onSessionCreated={handleSessionCreated}
      />
    </div>
  );
}
